"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AraasRemoteVideoTrack = exports.AraasLocalVideoTrack = exports.AraasVideoTrack = void 0;
const toolbox_1 = require("@streem/toolbox");
const streem_sdk_protobuf_1 = require("streem-sdk-protobuf");
const logger_1 = require("../../util/logger");
const start_animation_loop_1 = require("../util/start_animation_loop");
const media_track_1 = require("./media_track");
class AraasVideoTrack extends media_track_1.MediaTrack {
    constructor(videoElement, lightshowEngine, track) {
        super(track);
        this.videoElement = videoElement;
        this.lightshowEngine = lightshowEngine;
        this.track = track;
        this.kind = 'videoTrack';
        this.name = '';
        this.buffer = document.createElement('canvas');
        this.videoElementManager = new toolbox_1.VideoElementManager(this.videoElement);
    }
    scheduleRenderingCallback(callback) {
        const context = this.buffer.getContext('2d');
        (0, toolbox_1.invariant)(context, 'Unable to get 2d rendering context for buffer');
        this.videoElementManager.addHiddenVideoToDOM().playVideoWhenLoaded({
            onError: error => logger_1.logger.warn('failed to play the video: ', error),
        });
        const stopAnimationLoop = (0, start_animation_loop_1.startAnimationLoop)(() => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t;
            // https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/readyState
            if (this.videoElement.readyState < this.videoElement.HAVE_CURRENT_DATA) {
                logger_1.logger.debug('Video element is not in a readyState', this.videoElement.readyState);
                return;
            }
            // Sanity check that our video has some resolution greater than 0x0.
            if (this.videoElement.videoHeight <= 0 || this.videoElement.videoWidth <= 0) {
                logger_1.logger.debug('Video element is 0x0px', {
                    width: this.videoElement.videoWidth,
                    height: this.videoElement.videoHeight,
                });
                return;
            }
            this.latestCameraFrame =
                this.lightshowEngine.extractCameraFrame(this.videoElement) ||
                    this.latestCameraFrame;
            // Check for imageUpSide portrait orientation and correct bufferSize.
            const bufferSize = {
                width: this.videoElement.videoWidth,
                height: this.videoElement.videoHeight,
            };
            if (this.latestCameraFrame) {
                if (this.latestCameraFrame.imageUpSide &&
                    [
                        streem_sdk_protobuf_1.streem.room.CameraFrame.FrameSide.RIGHT,
                        streem_sdk_protobuf_1.streem.room.CameraFrame.FrameSide.LEFT,
                    ].includes(this.latestCameraFrame.imageUpSide)) {
                    bufferSize.width =
                        (_c = (_b = (_a = this.latestCameraFrame) === null || _a === void 0 ? void 0 : _a.cameraDimensions) === null || _b === void 0 ? void 0 : _b.height) !== null && _c !== void 0 ? _c : this.videoElement.videoHeight;
                    bufferSize.height =
                        ((_f = (_e = (_d = this.latestCameraFrame) === null || _d === void 0 ? void 0 : _d.cameraDimensions) === null || _e === void 0 ? void 0 : _e.width) !== null && _f !== void 0 ? _f : this.videoElement.videoWidth) -
                            this.lightshowEngine.getLongEdgeOffset((_j = (_h = (_g = this.latestCameraFrame) === null || _g === void 0 ? void 0 : _g.cameraDimensions) === null || _h === void 0 ? void 0 : _h.width) !== null && _j !== void 0 ? _j : this.videoElement.videoWidth);
                }
                else {
                    bufferSize.width =
                        ((_m = (_l = (_k = this.latestCameraFrame) === null || _k === void 0 ? void 0 : _k.cameraDimensions) === null || _l === void 0 ? void 0 : _l.width) !== null && _m !== void 0 ? _m : this.videoElement.videoWidth) -
                            this.lightshowEngine.getLongEdgeOffset((_q = (_p = (_o = this.latestCameraFrame) === null || _o === void 0 ? void 0 : _o.cameraDimensions) === null || _p === void 0 ? void 0 : _p.width) !== null && _q !== void 0 ? _q : this.videoElement.videoWidth);
                    bufferSize.height =
                        (_t = (_s = (_r = this.latestCameraFrame) === null || _r === void 0 ? void 0 : _r.cameraDimensions) === null || _s === void 0 ? void 0 : _s.height) !== null && _t !== void 0 ? _t : this.videoElement.videoHeight;
                }
            }
            if (this.buffer.width !== bufferSize.width ||
                this.buffer.height !== bufferSize.height) {
                this.buffer.width = bufferSize.width;
                this.buffer.height = bufferSize.height;
            }
            // Use the latest known orientation to correct the per-frame orientation.
            if (this.latestCameraFrame) {
                let rotationRadians = 0;
                switch (this.latestCameraFrame.imageUpSide) {
                    case streem_sdk_protobuf_1.streem.room.CameraFrame.FrameSide.RIGHT:
                        rotationRadians = -Math.PI / 2;
                        break;
                    case streem_sdk_protobuf_1.streem.room.CameraFrame.FrameSide.BOTTOM:
                        rotationRadians = Math.PI;
                        break;
                    case streem_sdk_protobuf_1.streem.room.CameraFrame.FrameSide.LEFT:
                        rotationRadians = Math.PI / 2;
                        break;
                }
                // Translate/rotate our canvas context.
                context.save();
                context.translate(bufferSize.width / 2, bufferSize.height / 2);
                context.rotate(rotationRadians);
                // Draw the video frame to the buffer canvas, cropping the lightshow pixels.
                const lightshowAvoidingRect = this.lightshowEngine.getLightshowAvoidingRect(this.videoElement);
                context.drawImage(this.videoElement, lightshowAvoidingRect.x, lightshowAvoidingRect.y, lightshowAvoidingRect.width, lightshowAvoidingRect.height, -(bufferSize.width / 2), -(bufferSize.height / 2), bufferSize.width, bufferSize.height);
                context.restore();
            }
            else {
                // 🥴 Onsite Streems and SWAC don't have a lightshow. Let's just display the video
                context.drawImage(this.videoElement, 0, 0);
            }
            callback(this.buffer, this.latestCameraFrame);
        }, this.videoElement);
        return () => {
            stopAnimationLoop();
            this.videoElementManager.removeVideoFromDOM();
        };
    }
}
exports.AraasVideoTrack = AraasVideoTrack;
class AraasLocalVideoTrack extends AraasVideoTrack {
    constructor(videoElement, lightshowEngine, track, stream) {
        super(videoElement, lightshowEngine, track);
        videoElement.srcObject = stream;
        videoElement.muted = true;
        this.videoElementManager.webkitVideoFix(() => logger_1.logger.info('Re-initialized video for Safari video fix'));
    }
    setEnabled(enable) {
        this.track.enabled = enable;
        this.enabledSubject.next(enable);
        if (enable) {
            this.playVideo(this.videoElement);
        }
    }
    playVideo(videoElement) {
        try {
            videoElement.play();
        }
        catch (error) {
            logger_1.logger.error('Failed to play the local video track for ARaaS: ', error);
        }
    }
    getMediaTrackConstraints() {
        return this.track.getConstraints();
    }
    restart(constraints) {
        logger_1.logger.warn("Attempting to restart video track but this method isn't supported yet for ARaaS");
        return Promise.resolve(false);
    }
}
exports.AraasLocalVideoTrack = AraasLocalVideoTrack;
class AraasRemoteVideoTrack extends AraasVideoTrack {
}
exports.AraasRemoteVideoTrack = AraasRemoteVideoTrack;
//# sourceMappingURL=video_tracks.js.map